import type { QueryClient } from '@tanstack/react-query'

import type { Page } from '@/api/client'
import { marketEndpoints } from '@/api/endpointCache'
import { getProposedPage } from '@/api/proposedEndpoint'
import type { components } from '@/api/schema'
import { displayDate, pageTotal } from '@/features/market/api/marketMappers'
import { CATEGORY_PARAM, isServedFeedQuery } from '@/features/news/filters'
import { daysFromToday } from '@/lib/relativeAge'
import { sentenceCase } from '@/lib/text'
import {
  newsFeedQuerySchema,
  newsPageSchema,
  type NewsFeedQuery,
  type NewsKind,
  type NewsPage,
} from '@/schemas/news'

type UpdateWire = components['schemas']['MarketUpdate']

const PAGE_SIZE = 10

const kindFrom = (row: UpdateWire): NewsKind => {
  if (row.entity_type === 'sector') return 'sector'
  if (row.entity_type === 'broker') return 'broker'
  return 'company'
}

/** mm-be's `kind` is the post's category, lower-cased ("filing", "sector_move"): worded here. */
const sourceLabel = (kind: string): string => {
  const words = kind.replaceAll('_', ' ').trim()
  return words === '' ? 'Update' : sentenceCase(words)
}

const categoryFor = (query: NewsFeedQuery): string | undefined =>
  query.scope === 'all' ? undefined : CATEGORY_PARAM[query.scope]

/** The feed's day group: "Today", "Yesterday", then the date — Kathmandu days. */
const dayLabelFor = (away: number | null, publishedAt: string): string => {
  if (away === 0) return 'Today'
  if (away === -1) return 'Yesterday'
  return displayDate(publishedAt)
}

const toStory = (row: UpdateWire, now: Date) => {
  const away = daysFromToday(row.published_at, now)
  return {
    id: String(row.id),
    kind: kindFrom(row),
    title: row.title,
    body: row.body,
    tags: row.entity === null ? [] : [row.entity],
    ...(row.entity !== null && row.entity_type === 'company' ? { symbol: row.entity } : {}),
    source: sourceLabel(row.kind),
    timeLabel: displayDate(row.published_at),
    publishedAt: row.published_at,
    day: away === 0 ? 'today' : 'earlier',
    dayLabel: dayLabelFor(away, row.published_at),
  }
}

/**
 * One page of the feed. A served query goes through the typed, cached `/market/updates/` call;
 * one that names a proposed param (`sort`, `show`, `sector`, `source`) can only be asked once
 * `filtersAvailable` is flipped, and then names them on the same path through the untyped seam.
 */
const fetchPage = (
  query: NewsFeedQuery,
  pageNumber: number,
  pageSize: number,
  client?: QueryClient,
): Promise<Page<UpdateWire>> => {
  const category = categoryFor(query)
  const served = {
    page: pageNumber,
    page_size: pageSize,
    ...(category === undefined ? {} : { category }),
    ...(query.query === undefined ? {} : { q: query.query }),
    ...(query.entity === undefined ? {} : { entity: query.entity }),
  }
  if (isServedFeedQuery(query)) return marketEndpoints(client).updates(served)
  return getProposedPage<UpdateWire>('/api/v5/market/updates/', {
    ...served,
    sort: query.sort,
    ...(query.show === undefined ? {} : { show: query.show }),
    ...(query.sectors === undefined ? {} : { sector: query.sectors.join(',') }),
    ...(query.sources === undefined ? {} : { source: query.sources.join(',') }),
  })
}

export const newsService = {
  /**
   * Whether mm-be takes the feed's proposed params yet (Oldest first, Show, Sector, Source —
   * `asks/nw1.md`). False: a query naming any of them is never requested, and the feed shows the
   * no-data state in its place. Flip this one constant when they ship.
   */
  filtersAvailable: false as boolean,

  /** Whether `feed` / `count` may be asked for this query at all. */
  canRequest: (query: NewsFeedQuery): boolean =>
    isServedFeedQuery(query) || newsService.filtersAvailable,

  feed: async (input: NewsFeedQuery, cursor?: string, client?: QueryClient): Promise<NewsPage> => {
    const query = newsFeedQuerySchema.parse(input)
    const pageNumber = cursor === undefined ? 1 : Number(cursor)
    const page = await fetchPage(query, pageNumber, PAGE_SIZE, client)
    const total = pageTotal(page.meta, page.rows.length)
    const hasNext = pageNumber * PAGE_SIZE < total
    const now = new Date()
    return newsPageSchema.parse({
      rows: page.rows.map((row) => toStory(row, now)),
      ...(hasNext ? { nextCursor: String(pageNumber + 1) } : {}),
      total,
    })
  },

  /**
   * How many stories a query matches: the Filters dialog's "Show 42 stories". One row asked for,
   * `meta.total` read. Never called for an unserved query (see `canRequest`).
   */
  count: async (input: NewsFeedQuery, client?: QueryClient): Promise<number> => {
    const query = newsFeedQuerySchema.parse(input)
    const page = await fetchPage(query, 1, 1, client)
    return pageTotal(page.meta, page.rows.length)
  },
}
